import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Stethoscope } from "lucide-react";
import { doctorsApi, staffApi, staffQueryKeys } from "../../shared/api/staff";
import { rolesApi, rolesQueryKeys } from "../../shared/api/roles";
import { branchesApi, branchesQueryKeys } from "../../shared/api/branches";
import { userBranchesApi, userBranchesQueryKeys } from "../../shared/api/userBranches";
import type {
  DoctorLicenseResponse,
  StaffRole,
  StaffUpdateRequest,
} from "../../shared/types/staff";
import {
  formatDate,
  formatDateTime,
  formatPhone,
  staffRoleLabel,
  staffStatusLabel,
  systemRoleLabel,
  systemRoleDescription,
} from "../../shared/format";
import { apiError } from "../../shared/api/client";
import { servicesApi, servicesQueryKeys } from "../../shared/api/services";
import {
  departmentsApi,
  departmentQueryKeys,
} from "../../shared/api/departments";
import { DepartmentSelect } from "../../shared/ui/DepartmentSelect";
import { LicenseBadge } from "./components/LicenseBadge";
import { InviteLinkBox } from "./components/InviteLinkBox";
import { Button } from "@/components/ui/button";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

type Tab = "profile" | "roles" | "doctor" | "licenses" | "invites" | "branches";

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

const STAFF_STATUS_VARIANT: Record<string, BadgeVariant> = {
  ACTIVE: "success",
  LEAVE: "warning",
  TERMINATED: "muted",
};

export function UserDetailPage() {
  const { id: rawId } = useParams<{ id: string }>();
  const id = rawId ?? "";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("profile");
  const [editing, setEditing] = useState(false);

  const staff = useQuery({
    queryKey: staffQueryKeys.detail(id),
    queryFn: () => staffApi.get(id),
  });

  // Find the doctor profile linked to this staff (if any).
  const allDoctors = useQuery({
    queryKey: staffQueryKeys.doctors(),
    queryFn: () => doctorsApi.list({ size: 200 }),
    enabled: staff.data?.staffRole === "DOCTOR",
  });
  const doctorRow = allDoctors.data?.content.find((d) => d.staffProfileId === id);
  const doctorId = doctorRow?.id ?? null;

  const licenses = useQuery({
    queryKey: doctorId ? staffQueryKeys.doctorLicenses(doctorId) : ["doctors", "none", "licenses"],
    queryFn: () => doctorsApi.listLicenses(doctorId!),
    enabled: Boolean(doctorId),
  });

  const leave = useMutation({
    mutationFn: () => staffApi.leave(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: staffQueryKeys.detail(id) }),
  });
  const returnFromLeave = useMutation({
    mutationFn: () => staffApi.returnFromLeave(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: staffQueryKeys.detail(id) }),
  });
  const terminate = useMutation({
    mutationFn: () => staffApi.terminate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: staffQueryKeys.detail(id) }),
  });
  const reactivate = useMutation({
    mutationFn: () => staffApi.reactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: staffQueryKeys.detail(id) }),
  });
  const remove = useMutation({
    mutationFn: () => staffApi.softDelete(id),
    onSuccess: () => navigate("/settings/users"),
  });

  // Guard AFTER hooks so hook order is stable (Rules of Hooks / React #310).
  if (!rawId) return <Navigate to="/settings/users" replace />;

  if (staff.isLoading) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Đang tải…</h1>
      </div>
    );
  }
  if (staff.isError || !staff.data) {
    return (
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Không tìm thấy nhân viên
          </h1>
          <p className="text-sm text-muted-foreground">
            {staff.error ? apiError(staff.error).detail : ""}
          </p>
        </div>
        <div className="shrink-0">
          <Button asChild variant="outline">
            <Link to="/settings/users">← Danh sách</Link>
          </Button>
        </div>
      </div>
    );
  }

  const s = staff.data;
  const isDoctor = s.staffRole === "DOCTOR";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{s.fullName}</h1>
          <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>{staffRoleLabel[s.staffRole]}</span>
            <span aria-hidden="true">·</span>
            <Badge variant={STAFF_STATUS_VARIANT[s.status] ?? "muted"}>
              {staffStatusLabel[s.status]}
            </Badge>
            {!s.userId && (
              <Badge variant="muted">Chưa kích hoạt tài khoản</Badge>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link to="/settings/users">← Danh sách</Link>
          </Button>
          {s.status === "ACTIVE" && (
            <Button
              type="button"
              variant="outline"
              onClick={() => leave.mutate()}
              disabled={leave.isPending}
            >
              Cho tạm nghỉ
            </Button>
          )}
          {s.status === "LEAVE" && (
            <Button
              type="button"
              variant="outline"
              onClick={() => returnFromLeave.mutate()}
              disabled={returnFromLeave.isPending}
            >
              Đi làm lại
            </Button>
          )}
          {s.status !== "TERMINATED" ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (confirm(`Đánh dấu ${s.fullName} đã nghỉ việc?`)) terminate.mutate();
              }}
              disabled={terminate.isPending}
            >
              Nghỉ việc
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => reactivate.mutate()}
              disabled={reactivate.isPending}
            >
              Kích hoạt lại
            </Button>
          )}
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              if (confirm(`Xoá mềm hồ sơ ${s.fullName}?`)) remove.mutate();
            }}
            disabled={remove.isPending}
          >
            Xoá
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border">
        <TabButton active={tab === "profile"} onClick={() => setTab("profile")}>
          Hồ sơ
        </TabButton>
        <TabButton active={tab === "roles"} onClick={() => setTab("roles")}>
          Vai trò
        </TabButton>
        {isDoctor && (
          <TabButton active={tab === "doctor"} onClick={() => setTab("doctor")}>
            Bác sĩ
          </TabButton>
        )}
        {isDoctor && (
          <TabButton active={tab === "licenses"} onClick={() => setTab("licenses")}>
            Giấy phép
          </TabButton>
        )}
        <TabButton active={tab === "invites"} onClick={() => setTab("invites")}>
          Lời mời
        </TabButton>
        <TabButton active={tab === "branches"} onClick={() => setTab("branches")}>
          Chi nhánh
        </TabButton>
      </div>

      {tab === "profile" && (
        editing ? (
          <ProfileEditForm
            id={id}
            initial={{
              fullName: s.fullName,
              staffRole: s.staffRole,
              email: s.email,
              phone: s.phone,
              birthDate: s.birthDate,
              nationalId: s.nationalId,
              addressLine: s.addressLine,
              notes: s.notes,
              primaryBranchId: s.primaryBranchId,
            }}
            onCancel={() => setEditing(false)}
            onSaved={() => {
              setEditing(false);
              qc.invalidateQueries({ queryKey: staffQueryKeys.detail(id) });
            }}
          />
        ) : (
          <Card>
            <CardContent className="p-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground">Thông tin cá nhân</h2>
                <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)}>
                  Sửa
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <KV label="Vai trò" value={staffRoleLabel[s.staffRole]} />
                <KV label="Trạng thái" value={staffStatusLabel[s.status]} />
                <KV label="Email" value={s.email ?? "—"} />
                <KV label="Số điện thoại" value={formatPhone(s.phone)} />
                <KV label="Ngày sinh" value={formatDate(s.birthDate)} />
                <KV label="CCCD / CMND" value={s.nationalId ?? "—"} />
                <KV label="Địa chỉ" value={s.addressLine ?? "—"} />
                <KV label="Tài khoản người dùng" value={s.userId ?? "Chưa kích hoạt"} />
                <KV label="Tạo lúc" value={formatDateTime(s.createdAt)} />
                <KV label="Cập nhật" value={formatDateTime(s.updatedAt)} />
              </div>
              {s.notes && (
                <div className="mt-5">
                  <h3 className="text-sm font-semibold text-foreground">Ghi chú</h3>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{s.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )
      )}

      {tab === "roles" && <RolesTab userId={s.userId} />}
      {tab === "doctor" && isDoctor && (
        <DoctorTab staffProfileId={id} doctorId={doctorId} loading={allDoctors.isLoading} />
      )}
      {tab === "licenses" && isDoctor && (
        <LicensesTab doctorId={doctorId} licenses={licenses.data ?? []} loading={licenses.isLoading} />
      )}
      {tab === "invites" && <StaffInvitesTab staffProfileId={id} />}
      {tab === "branches" && <BranchesTab userId={s.userId} />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={cn(
        "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-0.5">
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      <span className="block text-sm text-foreground">{value}</span>
    </div>
  );
}

function ProfileEditForm({
  id,
  initial,
  onCancel,
  onSaved,
}: {
  id: string;
  initial: StaffUpdateRequest & { staffRole: StaffRole };
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [data, setData] = useState<StaffUpdateRequest>(initial);
  const [errs, setErrs] = useState<Record<string, string>>({});

  const update = useMutation({
    mutationFn: (req: StaffUpdateRequest) => staffApi.update(id, req),
    onSuccess: () => onSaved(),
    onError: (e) => setErrs({ _form: apiError(e).detail }),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!data.fullName.trim()) {
          setErrs({ fullName: "Họ và tên không được để trống" });
          return;
        }
        update.mutate(data);
      }}
    >
      <Card>
        <CardContent className="space-y-4 p-6">
          <h3 className="text-lg font-semibold text-foreground">Sửa thông tin</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="fn">Họ &amp; tên</Label>
              <Input
                id="fn"
                type="text"
                value={data.fullName}
                onChange={(e) => setData({ ...data, fullName: e.target.value })}
                aria-invalid={Boolean(errs.fullName)}
              />
              {errs.fullName && <p className="text-sm text-destructive">{errs.fullName}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sr">Vai trò</Label>
              <Select
                value={data.staffRole}
                onValueChange={(v) => setData({ ...data, staffRole: v as StaffRole })}
              >
                <SelectTrigger id="sr">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(staffRoleLabel) as StaffRole[]).map((r) => (
                    <SelectItem key={r} value={r}>{staffRoleLabel[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="em">Email</Label>
              <Input
                id="em"
                type="email"
                value={data.email ?? ""}
                onChange={(e) => setData({ ...data, email: e.target.value || null })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ph">Số điện thoại</Label>
              <Input
                id="ph"
                type="tel"
                value={data.phone ?? ""}
                onChange={(e) => setData({ ...data, phone: e.target.value || null })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dob">Ngày sinh</Label>
              <Input
                id="dob"
                type="date"
                value={data.birthDate ?? ""}
                onChange={(e) => setData({ ...data, birthDate: e.target.value || null })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cccd">CCCD / CMND</Label>
              <Input
                id="cccd"
                type="text"
                value={data.nationalId ?? ""}
                onChange={(e) => setData({ ...data, nationalId: e.target.value || null })}
                maxLength={12}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="addr">Địa chỉ</Label>
            <Input
              id="addr"
              type="text"
              value={data.addressLine ?? ""}
              onChange={(e) => setData({ ...data, addressLine: e.target.value || null })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="notes">Ghi chú</Label>
            <Textarea
              id="notes"
              rows={3}
              value={data.notes ?? ""}
              onChange={(e) => setData({ ...data, notes: e.target.value || null })}
            />
          </div>
          {errs._form && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errs._form}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>Hủy</Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? "Đang lưu…" : "Lưu thay đổi"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}

function RolesTab({ userId }: { userId: string | null }) {
  const qc = useQueryClient();
  const allRoles = useQuery({
    queryKey: rolesQueryKeys.list(),
    queryFn: () => rolesApi.list(),
  });
  const userRoles = useQuery({
    queryKey: userId ? rolesQueryKeys.userRoles(userId) : ["users", "none", "roles"],
    queryFn: () => rolesApi.userRoles(userId!),
    enabled: Boolean(userId),
  });

  const [draft, setDraft] = useState<string[] | null>(null);
  useEffect(() => {
    if (userRoles.data) {
      setDraft(userRoles.data.roles.map((r) => r.code));
    }
  }, [userRoles.data]);

  const save = useMutation({
    mutationFn: (codes: string[]) => rolesApi.replaceUserRoles(userId!, { roleCodes: codes }),
    onSuccess: () => qc.invalidateQueries({ queryKey: rolesQueryKeys.userRoles(userId!) }),
  });

  if (!userId) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">
            Nhân viên chưa kích hoạt tài khoản. Vai trò RBAC chỉ áp dụng sau khi nhân viên chấp nhận lời mời.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (allRoles.isLoading || userRoles.isLoading || draft == null) {
    return <p className="text-sm text-muted-foreground">Đang tải…</p>;
  }

  function toggle(code: string) {
    setDraft((d) => (d == null ? d : d.includes(code) ? d.filter((c) => c !== code) : [...d, code]));
  }

  const dirty =
    draft && userRoles.data
      ? draft.length !== userRoles.data.roles.length ||
        draft.some((c) => !userRoles.data!.roles.find((r) => r.code === c))
      : false;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-foreground">Vai trò hệ thống</h2>
          {dirty && draft && (
            <Button
              type="button"
              onClick={() => save.mutate(draft)}
              disabled={save.isPending}
            >
              {save.isPending ? "Đang lưu…" : "Lưu thay đổi"}
            </Button>
          )}
        </div>
        <p className="text-[13px] text-muted-foreground">
          Vai trò xác định quyền hạn trong phần mềm. Một tài khoản có thể mang nhiều vai
          trò — tổng quyền là hợp của tất cả.
        </p>
        <div className="mt-4 flex flex-col gap-2">
          {allRoles.data?.map((r) => {
            const checked = draft.includes(r.code);
            const label = systemRoleLabel[r.code] ?? r.name;
            const desc = systemRoleDescription[r.code] ?? r.description ?? "";
            return (
              <label
                key={r.code}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-md border px-3.5 py-2.5",
                  checked ? "border-primary bg-primary/5" : "border-border bg-card"
                )}
              >
                <Checkbox
                  className="mt-0.5 shrink-0"
                  checked={checked}
                  onCheckedChange={() => toggle(r.code)}
                />
                <div>
                  <div className="text-sm font-semibold text-foreground">{label}</div>
                  {desc && (
                    <div className="mt-0.5 text-xs text-muted-foreground">{desc}</div>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function BranchesTab({ userId }: { userId: string | null }) {
  const qc = useQueryClient();
  const branchesQ = useQuery({
    queryKey: branchesQueryKeys.all(),
    queryFn: () => branchesApi.list(),
  });
  const assignedQ = useQuery({
    queryKey: userBranchesQueryKeys.of(userId ?? ""),
    queryFn: () => userBranchesApi.get(userId!),
    enabled: !!userId,
  });

  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (assignedQ.data) setSelected(new Set(assignedQ.data));
  }, [assignedQ.data]);

  const save = useMutation({
    mutationFn: () => userBranchesApi.set(userId!, Array.from(selected ?? [])),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userBranchesQueryKeys.of(userId ?? "") });
      setMsg("Đã lưu. Thay đổi áp dụng ở lần đăng nhập tiếp theo của người dùng.");
    },
  });

  if (!userId) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">
            Người dùng chưa kích hoạt tài khoản — chưa thể gán chi nhánh.
          </p>
        </CardContent>
      </Card>
    );
  }

  const branches = (branchesQ.data ?? []).filter((b) => b.status === "ACTIVE");
  const sel = selected ?? new Set<string>();
  const toggle = (id: string) => {
    const next = new Set(sel);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
    setMsg(null);
  };

  return (
    <Card>
      <CardContent className="grid gap-3 p-4">
        <h2 className="text-lg font-semibold text-foreground">
          Chi nhánh được phép truy cập
        </h2>
        <p className="text-sm text-muted-foreground">
          Không chọn chi nhánh nào = truy cập <strong>tất cả</strong> chi nhánh (mặc định
          cho chủ phòng khám).
        </p>
        {branchesQ.isLoading || assignedQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Đang tải…</p>
        ) : (
          <div className="grid gap-1.5">
            {branches.map((b) => (
              <label key={b.id} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={sel.has(b.id)}
                  onCheckedChange={() => toggle(b.id)}
                />
                <span>
                  {b.name} <span className="text-muted-foreground">({b.code})</span>
                </span>
              </label>
            ))}
          </div>
        )}
        {msg && <p className="text-sm text-emerald-600">{msg}</p>}
        <div className="flex justify-end">
          <Button
            type="button"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Đang lưu…" : "Lưu chi nhánh"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DoctorTab({
  doctorId,
  loading,
}: {
  staffProfileId: string;
  doctorId: string | null;
  loading: boolean;
}) {
  const qc = useQueryClient();
  // Hook first, then the early returns (Rules of Hooks / React #310).
  const doctorQuery = useQuery({
    queryKey: ["doctors", "detail", doctorId ?? ""],
    queryFn: () => doctorsApi.get(doctorId ?? ""),
    enabled: !!doctorId,
  });
  const [editingDept, setEditingDept] = useState(false);
  const [deptDraft, setDeptDraft] = useState<string | null>(null);
  const [deptError, setDeptError] = useState<string | null>(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [specialtyDraft, setSpecialtyDraft] = useState("");
  const [prescriberDraft, setPrescriberDraft] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  const saveDept = useMutation({
    mutationFn: () => {
      const d = doctorQuery.data!;
      return doctorsApi.update(d.id, {
        title: d.title,
        specialty: d.specialty ?? "",
        departmentId: deptDraft,
        bio: d.bio,
        signatureImageUrl: d.signatureImageUrl,
        nationalRxLinkCode: d.nationalRxLinkCode,
        nationalRxClinicCode: d.nationalRxClinicCode,
        nationalRxPrescriptionType: d.nationalRxPrescriptionType,
      });
    },
    onSuccess: () => {
      setEditingDept(false);
      setDeptError(null);
      qc.invalidateQueries({ queryKey: ["doctors", "detail", doctorId ?? ""] });
    },
    onError: (e) => setDeptError(apiError(e).detail),
  });

  const saveProfile = useMutation({
    mutationFn: async () => {
      const d = doctorQuery.data!;
      await doctorsApi.update(d.id, {
        title: titleDraft.trim() || "BS",
        specialty: specialtyDraft.trim(),
        departmentId: d.departmentId,
        bio: d.bio,
        signatureImageUrl: d.signatureImageUrl,
        nationalRxLinkCode: d.nationalRxLinkCode,
        nationalRxClinicCode: d.nationalRxClinicCode,
        nationalRxPrescriptionType: d.nationalRxPrescriptionType,
      });
      if (prescriberDraft !== d.prescriber) {
        await doctorsApi.setPrescriber(d.id, prescriberDraft);
      }
    },
    onSuccess: () => {
      setEditingProfile(false);
      setProfileError(null);
      qc.invalidateQueries({ queryKey: ["doctors", "detail", doctorId ?? ""] });
    },
    onError: (e) => setProfileError(apiError(e).detail),
  });

  if (loading) return <p className="text-sm text-muted-foreground">Đang tải hồ sơ bác sĩ…</p>;
  if (!doctorId) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="mx-auto max-w-sm py-8 text-center text-muted-foreground">
            <Stethoscope className="mx-auto mb-2.5 h-8 w-8" aria-hidden="true" />
            <div className="mb-1.5 font-semibold text-foreground">Chưa có hồ sơ bác sĩ</div>
            <div className="text-[13px]">
              Hồ sơ bác sĩ sẽ được tạo tự động khi mời nhân viên với vị trí <em>Bác sĩ</em>.
              Nếu cần tạo thủ công, hãy liên hệ quản trị viên hệ thống.
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Hồ sơ bác sĩ</h2>
          {doctorQuery.data && !editingProfile && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                const d = doctorQuery.data!;
                setTitleDraft(d.title ?? "");
                setSpecialtyDraft(d.specialty ?? "");
                setPrescriberDraft(d.prescriber);
                setProfileError(null);
                setEditingProfile(true);
              }}
            >
              Sửa
            </Button>
          )}
        </div>
        {doctorQuery.isLoading && <p className="text-sm text-muted-foreground">Đang tải…</p>}
        {doctorQuery.data && !editingProfile && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <KV label="Học vị / Chức danh" value={doctorQuery.data.title ?? "—"} />
            <KV label="Chuyên khoa" value={doctorQuery.data.specialty ?? "—"} />
            <KV
              label="Được kê đơn thuốc"
              value={doctorQuery.data.prescriber ? "✓ Có" : "Không"}
            />
          </div>
        )}
        {doctorQuery.data && editingProfile && (
          <div className="grid max-w-md gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="doc-title">Học vị / Chức danh</Label>
              <Input
                id="doc-title"
                type="text"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                placeholder="VD: BS, ThS.BS, TS.BS"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="doc-specialty">Chuyên khoa</Label>
              <Input
                id="doc-specialty"
                type="text"
                value={specialtyDraft}
                onChange={(e) => setSpecialtyDraft(e.target.value)}
                placeholder="VD: Da liễu"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={prescriberDraft}
                onCheckedChange={(v) => setPrescriberDraft(v === true)}
              />
              <span>Được kê đơn thuốc</span>
            </label>
            {profileError && (
              <p className="text-sm text-destructive">{profileError}</p>
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                disabled={saveProfile.isPending || !specialtyDraft.trim()}
                onClick={() => saveProfile.mutate()}
              >
                {saveProfile.isPending ? "Đang lưu…" : "Lưu"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingProfile(false);
                  setProfileError(null);
                }}
              >
                Huỷ
              </Button>
            </div>
          </div>
        )}
        {doctorQuery.data && !editingProfile && (
          <>
            <div className="mt-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-foreground">Khoa</h3>
                {!editingDept && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDeptDraft(doctorQuery.data!.departmentId);
                      setEditingDept(true);
                    }}
                  >
                    Sửa
                  </Button>
                )}
              </div>
              {editingDept ? (
                <div className="mt-2 grid max-w-sm gap-2">
                  <DepartmentSelect value={deptDraft} onChange={setDeptDraft} />
                  {deptError && (
                    <p className="text-sm text-destructive">{deptError}</p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      disabled={saveDept.isPending}
                      onClick={() => saveDept.mutate()}
                    >
                      {saveDept.isPending ? "Đang lưu…" : "Lưu"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingDept(false);
                        setDeptError(null);
                      }}
                    >
                      Huỷ
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="mt-1 text-sm text-foreground">
                  <DoctorDepartmentLabel departmentId={doctorQuery.data.departmentId} />
                </p>
              )}
            </div>

            {doctorQuery.data.bio && (
              <div className="mt-5">
                <h3 className="text-sm font-semibold text-foreground">Giới thiệu</h3>
                <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                  {doctorQuery.data.bio}
                </p>
              </div>
            )}

            <DoctorServicesSection doctorId={doctorQuery.data.id} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** Show a doctor's khoa name (resolved from the active department options). */
function DoctorDepartmentLabel({ departmentId }: { departmentId: string | null }) {
  const opts = useQuery({
    queryKey: departmentQueryKeys.options(),
    queryFn: () => departmentsApi.options(),
    enabled: !!departmentId,
  });
  if (!departmentId) return <span className="text-muted-foreground">—</span>;
  const d = opts.data?.find((x) => x.id === departmentId);
  return <>{d ? `${d.name} (${d.code})` : "—"}</>;
}

/** "Dịch vụ bác sĩ cung cấp" — checkbox list of active services, saved as a replace-set. */
function DoctorServicesSection({ doctorId }: { doctorId: string }) {
  const qc = useQueryClient();
  const services = useQuery({
    queryKey: servicesQueryKeys.all(),
    queryFn: () => servicesApi.listAll(),
  });
  const assigned = useQuery({
    queryKey: staffQueryKeys.doctorServices(doctorId),
    queryFn: () => doctorsApi.listServices(doctorId),
  });

  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seed the working set once the assigned list arrives.
  const seed = assigned.data;
  const current =
    selected ?? new Set((seed ?? []).map((s) => s.serviceId));

  const save = useMutation({
    mutationFn: () => doctorsApi.setServices(doctorId, Array.from(current)),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: staffQueryKeys.doctorServices(doctorId) });
    },
    onError: (e) => setError(apiError(e).detail),
  });

  function toggle(id: string) {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  const activeServices = (services.data ?? []).filter((s) => s.active);

  return (
    <div className="mt-6">
      <h3 className="mb-2 text-sm font-semibold text-foreground">Dịch vụ bác sĩ cung cấp</h3>
      {(services.isLoading || assigned.isLoading) && (
        <p className="text-sm text-muted-foreground">Đang tải…</p>
      )}
      {!services.isLoading && activeServices.length === 0 && (
        <p className="text-[13px] text-muted-foreground">
          Chưa có dịch vụ nào đang hoạt động.
        </p>
      )}
      {!assigned.isLoading && activeServices.length > 0 && (
        <>
          <div className="grid max-h-64 grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-1.5 overflow-auto rounded-md border border-border p-1">
            {activeServices.map((s) => (
              <label
                key={s.id}
                className="flex items-center gap-2 text-[13px]"
              >
                <Checkbox
                  checked={current.has(s.id)}
                  onCheckedChange={() => toggle(s.id)}
                />
                <span>
                  {s.name}{" "}
                  <span className="font-mono text-muted-foreground">{s.code}</span>
                </span>
              </label>
            ))}
          </div>
          {error && (
            <p className="mt-2 text-sm text-destructive">{error}</p>
          )}
          <div className="mt-2">
            <Button
              type="button"
              size="sm"
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? "Đang lưu…" : "Lưu dịch vụ"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function LicensesTab({
  doctorId,
  licenses,
  loading,
}: {
  doctorId: string | null;
  licenses: DoctorLicenseResponse[];
  loading: boolean;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [num, setNum] = useState("");
  const [issuedBy, setIssuedBy] = useState("");
  const [issuedAt, setIssuedAt] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      doctorsApi.createLicense(doctorId!, {
        licenseNumber: num,
        issuedBy: issuedBy || null,
        issuedAt,
        validUntil: validUntil || null,
        notes: null,
      }),
    onSuccess: () => {
      setAdding(false);
      setNum("");
      setIssuedBy("");
      setIssuedAt("");
      setValidUntil("");
      qc.invalidateQueries({ queryKey: staffQueryKeys.doctorLicenses(doctorId!) });
    },
    onError: (e) => setError(apiError(e).detail),
  });

  const deactivate = useMutation({
    mutationFn: (licenseId: string) => doctorsApi.deactivateLicense(doctorId!, licenseId),
    onSuccess: () => qc.invalidateQueries({ queryKey: staffQueryKeys.doctorLicenses(doctorId!) }),
  });

  if (!doctorId) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">
            Chưa có hồ sơ bác sĩ. Không thể quản lý giấy phép.
          </p>
        </CardContent>
      </Card>
    );
  }
  if (loading) return <p className="text-sm text-muted-foreground">Đang tải giấy phép…</p>;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Giấy phép hành nghề</h2>
          <Button type="button" variant="outline" onClick={() => setAdding((v) => !v)}>
            {adding ? "Đóng" : "+ Thêm giấy phép"}
          </Button>
        </div>

        {adding && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!num.trim() || !issuedAt) {
                setError("Số giấy phép và ngày cấp là bắt buộc");
                return;
              }
              setError(null);
              create.mutate();
            }}
            className="mb-4 rounded-md bg-muted p-4"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5">
                <Label htmlFor="lnum">Số giấy phép *</Label>
                <Input id="lnum" type="text" value={num} onChange={(e) => setNum(e.target.value)} maxLength={64} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lib">Cơ quan cấp</Label>
                <Input id="lib" type="text" value={issuedBy} onChange={(e) => setIssuedBy(e.target.value)} placeholder="VD: Sở Y tế Hà Nội" maxLength={256} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lia">Ngày cấp *</Label>
                <Input id="lia" type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lvu">Hạn đến</Label>
                <Input id="lvu" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
              </div>
            </div>
            {error && (
              <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
            <div className="mt-3 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAdding(false)}>Hủy</Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Đang lưu…" : "Thêm"}
              </Button>
            </div>
          </form>
        )}

        {licenses.length === 0 && !adding && (
          <p className="text-sm text-muted-foreground">
            Chưa có giấy phép nào. Bấm "Thêm giấy phép" để bắt đầu.
          </p>
        )}

        <div className="divide-y divide-border">
          {licenses.map((l) => (
            <div key={l.id} className="flex items-start justify-between gap-4 py-3">
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                  <span>{l.licenseNumber}</span>
                  <LicenseBadge validUntil={l.validUntil} expired={l.expired} />
                  {!l.active && (
                    <Badge variant="muted">Đã vô hiệu</Badge>
                  )}
                </div>
                <div className="text-[13px] text-muted-foreground">
                  {l.issuedBy && `Cấp bởi ${l.issuedBy} · `}
                  Ngày cấp {formatDate(l.issuedAt)}
                  {l.validUntil && ` · Hạn ${formatDate(l.validUntil)}`}
                  {l.verifiedAt && ` · Đã xác minh ${formatDate(l.verifiedAt)}`}
                </div>
              </div>
              <div className="shrink-0">
                {l.active && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => deactivate.mutate(l.id)}
                    disabled={deactivate.isPending}
                  >
                    Vô hiệu
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function StaffInvitesTab({ staffProfileId }: { staffProfileId: string }) {
  // Reuse the public invite-list endpoint filtered to all statuses for this
  // staff. For Lite v1 we don't have a per-staff endpoint, so list SENT + ACCEPTED.
  const sent = useQuery({
    queryKey: staffQueryKeys.invites("SENT"),
    queryFn: () => staffApi.listInvites("SENT"),
  });
  const accepted = useQuery({
    queryKey: staffQueryKeys.invites("ACCEPTED"),
    queryFn: () => staffApi.listInvites("ACCEPTED"),
  });

  const all = [...(sent.data ?? []), ...(accepted.data ?? [])]
    .filter((i) => i.staffProfileId === staffProfileId)
    .sort((a, b) => b.sentAt.localeCompare(a.sentAt));

  const [showLink, setShowLink] = useState<string | null>(null);

  const resend = useMutation({
    mutationFn: (id: string) => staffApi.resendInvite(id),
    onSuccess: (res) => setShowLink(res.inviteLink),
  });

  return (
    <Card>
      <CardContent className="p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-foreground">Lịch sử lời mời</h2>
        </div>
        {showLink && <InviteLinkBox link={showLink} />}
        {sent.isLoading || accepted.isLoading ? (
          <p className="text-sm text-muted-foreground">Đang tải…</p>
        ) : all.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Chưa có lời mời nào cho nhân viên này.
          </p>
        ) : (
          <div className="divide-y divide-border">
            {all.map((i) => (
              <div key={i.id} className="flex items-start justify-between gap-4 py-3">
                <div className="min-w-0 space-y-1">
                  <div className="text-sm font-medium text-foreground">{i.email}</div>
                  <div className="flex flex-wrap items-center gap-1 text-[13px] text-muted-foreground">
                    <span>
                      Gửi {formatDateTime(i.sentAt)} · Hết hạn {formatDate(i.expiresAt)} ·
                      Trạng thái:
                    </span>
                    <Badge variant="outline">{i.status}</Badge>
                  </div>
                </div>
                <div className="shrink-0">
                  {i.status === "SENT" && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => resend.mutate(i.id)}
                      disabled={resend.isPending}
                    >
                      Gửi lại link
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
